import { BehaviorSubject, Observable } from 'rxjs';

import { AlertType } from '@cart-checkout/checkout/data-access/enums';
import { EDDAlertModel } from '@cart-checkout/checkout/data-access/interfaces';
import { EDDSku } from '@cart-checkout/data-access/interfaces';
import { Injectable } from '@angular/core';

@Injectable({
	providedIn: 'root'
})
export class EDDAlertService {
	/**
	 * @description
	 * default title to pass the alert component
	 */
	public readonly defaultTitle = 'Estimated delivery date has been updated based on your zip code.';

	public readonly eddDelayTitle = 'P.O. Box will have longer delivery time';

	/**
	 * @description
	 * returns a public observable for the ui
	 */
	public get showEDDAlert$(): Observable<boolean> {
		return this.showEDDAlerts$.asObservable();
	}

	/**
	 * @description
	 * returns the current value of the observable
	 */
	public get showEDDAlert(): boolean {
		return this.showEDDAlerts$.value;
	}

	get showEddDelayAlertShipping$(): Observable<boolean> {
		return this.showEddDelayAlertsShipping$.asObservable();
	}

	get showEddDelayAlertBilling$(): Observable<boolean> {
		return this.showEddDelayAlertsBilling$.asObservable();
	}

	/**
	 * @description
	 * returns public number of times displayed
	 */
	public get timesDisplayed(): number {
		return this.noOfTimesDisplayed;
	}

	/**
	 * @description
	 * returns public boolean if the edd alert has been displayed
	 * local storage is utilized to persist if the application reloads
	 */
	public get wasDisplayed(): boolean {
		return localStorage.getItem(this.localStorageKey) !== null || this.timesDisplayed > 0;
	}

	/**
	 * @description
	 * keeps the state of whether to show the edd PO Box alert or not
	 */
	showEddDelayAlertsShipping$ = new BehaviorSubject<boolean>(false);

	showEddDelayAlertsBilling$ = new BehaviorSubject<boolean>(false);

	/**
	 * @description
	 * keeps the state of whether to show the edd alert or not
	 */
	private showEDDAlerts$ = new BehaviorSubject<boolean>(false);

	/**
	 * @description
	 * Set of unique edd dates
	 */
	private previousEDDDates = new Set();

	/**
	 * @description
	 * keeps track of how many times the alert has been displayed
	 */
	private noOfTimesDisplayed = 0;

	/**
	 * @description
	 * stores key for local storage
	 */
	private readonly localStorageKey = 'DCSG_NGX_EDD_ALERT_DISPLAYED';

	/**
	 * @description
	 * keeps a collection of previous unique estimated delivery dates in a Set
	 * determines if we should show the edd alert
	 * @param eddSkus
	 */
	public getEDDChangeMessages(eddSkus: EDDSku[]): void {
		// cycle through edds for each sku and add unique sku with dates array as a string
		eddSkus.forEach((eddSku: EDDSku) => {
			const skuDate = `${eddSku.sku}_${eddSku.dates.toString()}`;

			this.previousEDDDates.add(skuDate);
		});

		// determine if the Set size has changed
		this.showEDDAlerts$.next(this.previousEDDDates.size !== eddSkus.length);

		// increment times displayed
		if (this.showEDDAlert) {
			++this.noOfTimesDisplayed;
		}

		// add to local storage if the edd alert was displayed
		if (this.timesDisplayed === 1) {
			this.addToLocalStorage();
		}
	}

	/**
	 * @description
	 * accepts a message and returns an object to display an edd alert
	 * @param msg
	 */
	public getAlertMessageObject(msg = '', showEddDelayAlert = false): EDDAlertModel {
		return {
			title: showEddDelayAlert ? this.eddDelayTitle : `${this.defaultTitle}`,
			alertType: showEddDelayAlert ? AlertType.WARN : AlertType.INFO,
			message: `${msg}`
		} as EDDAlertModel;
	}

	/**
	 * @description
	 * clear out the Set, set showEDDAlert$ to false, reset times displayed, and remove from local storage
	 */
	public resetState(): void {
		this.previousEDDDates.clear();
		this.showEDDAlerts$.next(false);
		this.noOfTimesDisplayed = 0;
		this.removeFromLocalStorage();
	}

	/**
	 * @description
	 * add an item to storage to represent that the edd alert was displayed
	 */
	private addToLocalStorage(): void {
		localStorage.setItem(this.localStorageKey, this.localStorageKey);
	}

	/**
	 * @description
	 * remove eddAlertDisplayed from local storage if it exists
	 */
	private removeFromLocalStorage(): void {
		if (localStorage.getItem(this.localStorageKey) !== null) {
			localStorage.removeItem(this.localStorageKey);
		}
	}
}
